import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Lock, LineChart, TrendingUp, Globe2, Award, Mail,
  DollarSign, Users, GraduationCap, Zap, ArrowRight, Rocket,
  Building2, Target,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = 'https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png';
const PH_URL = 'https://www.producthunt.com/@i_amin';
const MAILTO = 'mailto:amin@he-direct.com?subject=H%20E%20Direct%20Consultants%20-%20Investor%20conversation&body=Hi%20Amin%2C%0A%0AI%20went%20through%20the%20investor%20page%20for%20H%20E%20Direct%20Consultants%20and%20would%20like%20to%20discuss.%0A%0A';

const Section = ({ eyebrow, title, children }) => (
  <section className="py-16">
    <div className="max-w-5xl mx-auto px-6">
      {eyebrow && (
        <div className="text-xs font-semibold uppercase tracking-widest text-amber-500">{eyebrow}</div>
      )}
      {title && <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white">{title}</h2>}
      <div className="mt-8">{children}</div>
    </div>
  </section>
);

const StatBig = ({ n, label, sub }) => (
  <div className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm">
    <div className="text-4xl md:text-5xl font-bold text-amber-400 tabular-nums">{n}</div>
    <div className="mt-3 text-xs uppercase tracking-widest text-slate-300">{label}</div>
    {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
  </div>
);

const Row = ({ k, v, muted }) => (
  <div className={`flex items-center justify-between py-3 border-b border-white/10 ${muted ? 'text-slate-400' : 'text-slate-100'}`}>
    <span className="text-sm">{k}</span>
    <span className="text-sm font-semibold tabular-nums">{v}</span>
  </div>
);

export default function InvestorsPage() {
  const [locked, setLocked] = useState(true);
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = 'H E Direct Consultants — Investors';
    if (localStorage.getItem('hed_investor_access') === '1' || new URLSearchParams(window.location.search).get('key') === 'HED2026') {
      localStorage.setItem('hed_investor_access', '1');
      setLocked(false);
    }
  }, []);

  useEffect(() => {
    if (!locked) {
      axios.get(`${API}/api/public/traction`).then((r) => setStats(r.data)).catch(() => {});
    }
  }, [locked]);

  const unlock = async () => {
    setChecking(true);
    setErr('');
    try {
      const res = await axios.post(`${API}/api/public/investor-access`, { code });
      if (res.data?.allowed) {
        localStorage.setItem('hed_investor_access', '1');
        setLocked(false);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Invalid code');
    } finally {
      setChecking(false);
    }
  };

  const big = (v) => (typeof v === 'number' ? v.toLocaleString() : '—');

  if (locked) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <img src={LOGO} alt="H E Direct Consultants" className="h-14 w-auto mb-8" />
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-500">Restricted access</div>
          <h1 className="mt-2 text-3xl font-bold">Investor page</h1>
          <div className="mt-1 text-sm text-slate-400">H E Direct Consultants · Study Abroad Platform for Pakistani Students</div>
          <p className="mt-4 text-slate-400 text-sm">
            This page contains company financials and traction data. Enter the access code you received to continue, or email
            <a href={MAILTO} className="text-amber-400 underline hover:text-amber-300 ml-1">amin@he-direct.com</a> to request one.
          </p>
          <div className="mt-6 flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && unlock()}
              placeholder="Access code"
              className="flex-1 bg-white/5 border border-white/15 rounded-lg px-4 py-3 focus:outline-none focus:border-amber-400"
              data-testid="investor-code-input"
            />
            <button
              onClick={unlock}
              disabled={checking || !code.trim()}
              className="px-5 py-3 rounded-lg bg-amber-400 text-slate-900 font-semibold hover:bg-amber-300 disabled:opacity-50"
              data-testid="investor-code-submit"
            >
              <Lock className="w-4 h-4 inline mr-1" /> Unlock
            </button>
          </div>
          {err && <div className="mt-3 text-sm text-rose-400">{err}</div>}
          <div className="mt-10 text-xs text-slate-500">
            Not an investor? <Link to="/launch" className="underline hover:text-slate-300">See the public launch page →</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0"
             style={{ backgroundImage: 'radial-gradient(700px 400px at 20% 10%, rgba(245,158,11,0.15), transparent), radial-gradient(500px 300px at 90% 30%, rgba(16,185,129,0.15), transparent)' }} />
        <div className="relative max-w-5xl mx-auto px-6 pt-12 pb-16">
          <div className="flex items-center justify-between mb-10">
            <img src={LOGO} alt="H E Direct" className="h-11 w-auto" />
            <div className="flex items-center gap-2 text-xs">
              <Link to="/launch" className="text-slate-400 hover:text-white">← Public launch page</Link>
            </div>
          </div>
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-500">Investor Pitch — Confidential</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold">H E Direct Consultants</h1>
          <div className="mt-2 text-lg text-amber-300 font-medium">Study Abroad Platform for Pakistani Students</div>
          <p className="mt-6 text-slate-300 max-w-2xl">
            A vertical CRM + operations platform running a live study-abroad consultancy across Peshawar
            and Islamabad — with 46 sub-agents processing 900+ applications for the September 2026 UK intake.
            Not a pitch deck. A running business.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <a href={MAILTO} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-slate-900 font-semibold hover:bg-amber-300">
              <Mail className="w-4 h-4" /> amin@he-direct.com
            </a>
            <a href={PH_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 text-white hover:bg-white/10">
              <Award className="w-4 h-4" /> Product Hunt @i_amin
            </a>
          </div>
        </div>
      </div>

      {/* Live traction */}
      <Section eyebrow="Live Traction" title="Not projections. Running in production today.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatBig n={big(stats?.leads_tracked)} label="Leads tracked" sub="Sept 2026 pipeline" />
          <StatBig n={big(stats?.applications_processed)} label="Applications" sub="Sept 2026 intake" />
          <StatBig n={big(stats?.active_agents)} label="Active agents" sub="Sub-agent network" />
          <StatBig n={big(stats?.paying_students)} label="Paying students" sub="Deposit paid" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          <StatBig n={big(stats?.uk_courses_indexed)} label="UK courses indexed" />
          <StatBig n={big(stats?.universities_indexed)} label="Universities" />
          <StatBig n={big(stats?.students_served)} label="Unique students" />
        </div>
      </Section>

      {/* Problem */}
      <Section eyebrow="The Problem" title="Study-abroad agencies run on Excel and WhatsApp.">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-white/10 bg-white/5 p-6">
            <div className="text-amber-400 font-semibold text-sm">The market is huge — and unserved</div>
            <p className="mt-3 text-slate-300 text-sm leading-relaxed">
              <strong className="text-white">10,000+</strong> study-abroad consultancies operate in South Asia + MENA alone. They send
              <strong className="text-white"> 400k+ students</strong> to UK universities every year. Yet almost none use vertical CRM —
              the market is stuck on Salesforce, Zoho, or paper.
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-6">
            <div className="text-amber-400 font-semibold text-sm">Horizontal CRMs miss the specifics</div>
            <p className="mt-3 text-slate-300 text-sm leading-relaxed">
              Study-abroad has 12-stage journeys, agent sub-networks with commission splits, visa refunds,
              multi-intake batching, and university-specific document rules. Generic CRMs bend badly to fit.
            </p>
          </div>
        </div>
      </Section>

      {/* Solution */}
      <Section eyebrow="The Solution" title="A CRM built from the operator seat.">
        <p className="text-slate-300 max-w-3xl">
          H E Direct Consultants was built inside a running study-abroad consultancy. Every workflow — from
          lead capture, agent onboarding with digital policy signing, visa journey tracking, refund handling,
          and monthly P&amp;L accounting — comes from problems we hit ourselves.
        </p>
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <div className="rounded-xl border border-white/10 bg-white/5 p-5">
            <Users className="w-5 h-5 text-teal-400" />
            <div className="mt-3 font-semibold">Lead ops</div>
            <div className="mt-1 text-sm text-slate-400">Import 2k+ leads from Excel, dedupe, assign to counselors, chase in bulk with WhatsApp + email.</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-5">
            <GraduationCap className="w-5 h-5 text-indigo-400" />
            <div className="mt-3 font-semibold">Application journey</div>
            <div className="mt-1 text-sm text-slate-400">12 tracked stages: Inquiry → CAS → Refund → Visa → Enrolment, with document-per-stage rules.</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-5">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            <div className="mt-3 font-semibold">Live P&amp;L</div>
            <div className="mt-1 text-sm text-slate-400">Every paid consultancy fee auto-flows into a monthly P&amp;L dashboard. Zero manual accounting.</div>
          </div>
        </div>
      </Section>

      {/* Business model */}
      <Section eyebrow="Business Model" title="Vertical SaaS + Transaction Fees.">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="text-amber-400 font-semibold">Path 1 — Subscription</div>
            <div className="mt-4 space-y-1">
              <Row k="Solo agent" v="$29 / month" />
              <Row k="Small consultancy (up to 5 seats)" v="$99 / month" />
              <Row k="Multi-branch (10+ seats)" v="$299 / month" />
              <Row k="Enterprise / white-label" v="Custom" />
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="text-amber-400 font-semibold">Path 2 — University Commission Share</div>
            <p className="mt-4 text-slate-300 text-sm leading-relaxed">
              Optional revenue-share on university placements sourced through the platform&rsquo;s
              built-in course discovery.
              Average agent commission is <strong className="text-white">£1,200–£2,500</strong> per enrolled UK student.
              A 5% platform take on
              even a fraction of the ~400k annual UK intake represents a $50M+ opportunity.
            </p>
          </div>
        </div>
      </Section>

      {/* Ask */}
      <Section eyebrow="What we&rsquo;re raising" title="Seed round · $250k for 12 months of runway.">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-amber-400/40 bg-amber-500/5 p-6">
            <Zap className="w-5 h-5 text-amber-400" />
            <div className="mt-3 font-semibold">Product</div>
            <div className="mt-1 text-sm text-slate-400">Sales + billing + partner integrations. Two engineers for 12 months.</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <Target className="w-5 h-5 text-teal-400" />
            <div className="mt-3 font-semibold">Go-to-market</div>
            <div className="mt-1 text-sm text-slate-400">Onboard 50 partner agencies in South Asia. WhatsApp-first sales.</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <Building2 className="w-5 h-5 text-indigo-400" />
            <div className="mt-3 font-semibold">Ops</div>
            <div className="mt-1 text-sm text-slate-400">Support desk, compliance, and legal scaffolding for multi-country ops.</div>
          </div>
        </div>
      </Section>

      {/* Contact */}
      <Section eyebrow="Contact" title="Let&rsquo;s talk.">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-amber-500/10 via-transparent to-teal-500/10 p-10">
          <p className="text-slate-300 max-w-2xl">
            I&rsquo;m Amin — founder of H E Direct. Reply directly to my inbox and we&rsquo;ll set up a call
            with the live production dashboard on screen.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={MAILTO} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-slate-900 font-semibold hover:bg-amber-300">
              <Mail className="w-4 h-4" /> amin@he-direct.com
            </a>
            <a href={PH_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white hover:bg-white/10">
              <Award className="w-4 h-4" /> @i_amin on Product Hunt
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </Section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Higher Education Direct — Confidential investor pack</div>
          <Link to="/launch" className="hover:text-slate-300">Public launch page →</Link>
        </div>
      </footer>
    </div>
  );
}
